import { View, SxProp, useDripsyTheme, Image } from 'dripsy';
import React, { useState, useContext, useEffect } from 'react';
import moment, { Moment } from 'moment-timezone';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { H3, H5, H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { CurrencySelector } from '@/design-system/components/currencySelector/CurrencySelector';
import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Row } from '@/design-system/components/layout';
import { DateRangePicker } from '@/design-system/components/dateRangePicker';
import { UniCheckbox, UniInput } from '@/design-system/components/inputs';
import { TableView } from '@/design-system/components/table/TableView';
import { Icon } from '@/design-system/components/icons/Icon';
import { Divider } from '@/design-system/components/Divider';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { CurrenciesValues } from '@/features/account-management/options';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/design-system/components/tooltip/Tooltip';
import { LastUpdated } from '@/design-system/components/lastUpdated/LastUpdated';
import { TokenPagination } from '@/design-system/components/pagination/TokenPagination';

import { useAccountBalance, useAccountTransaction } from '@/features/account-management/screens/details/hooks/useAccount.hook';
import { useCard } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { UiAccountAssetBalance } from '@utx/utx-access-client';
import { RootStackRoutes } from 'types';
import { useNavigation } from '@react-navigation/native';
import { UnionType } from '@/features/bill-payment/enum';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { UtxHasFeature } from '@/design-system/components/hasFeature/utxHasFeature';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';

export interface AccountDetailsProps {
  account: string;
  tab?: number;
  record: any;
}

const NoData = () => {
  const { t } = useTranslation('earn');
  return (
    <View sx={styles.noData}>
      <Icon width={60} height={60} name="listZeroState" />
      <TextBody semiBold>{t('earn-invest::glossary.search-no-result')}</TextBody>
    </View>
  );
};

export const AccountDetails: React.FC<AccountDetailsProps> = (props) => {
  const { account, tab, record } = props;
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const showChange = (value: boolean) => {
    setShow(value);
  };
  const [currency, setCurrency] = useState('HKD');

  const isPositive = (value: string) => {
    return value.startsWith('+');
  };
  const isNegative = (value: string) => {
    return (value && value.toString().startsWith('-')) || false;
  };
  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };

  const [searchAsset, setSearchAsset] = useState<string | undefined>(undefined);
  const { balances, isLoadingAccountBalances, reloadAccountBalances, accountsTransfers } = useAccountBalance({
    accountId: account,
    asset: searchAsset
  });
  const [accountBalances, setAccountBalances] = useState<UiAccountAssetBalance[]>([]);
  useEffect(() => {
    setAccountBalances(
      (balances && balances[0] && balances[0].assets?.sort((a, b) => (b?.balanceUsdEquivalent || 0) - (a.balanceUsdEquivalent || 0))) || []
    );
  }, [balances]);
  const [assetsData, setAssetsData] = useState<UiAccountAssetBalance[]>([]);

  const [accountsOverview, setAccountsOverview] = useState<{
    creditBalanceInHkd: number;
    creditBalanceInUsd: number;
    assetBalanceInHkd: number;
    assetBalanceInUsd: number;
  }>({ creditBalanceInHkd: 0, creditBalanceInUsd: 0, assetBalanceInHkd: 0, assetBalanceInUsd: 0 });
  useEffect(() => {
    if (!isLoadingAccountBalances) {
      let assetHkdEquivalentAmount = 0;
      let assetUsdEquivalentAmount = 0;
      if (accountBalances && accountBalances.length) {
        accountBalances.forEach((accountBalance: any) => {
          assetHkdEquivalentAmount += accountBalance?.balanceHkdEquivalent;
          assetUsdEquivalentAmount += accountBalance?.balanceUsdEquivalent;
        });
        setAccountsOverview({
          ...accountsOverview,
          assetBalanceInHkd: assetHkdEquivalentAmount,
          assetBalanceInUsd: assetUsdEquivalentAmount
        });
      } else {
        setAccountsOverview({
          ...accountsOverview,
          assetBalanceInHkd: assetHkdEquivalentAmount,
          assetBalanceInUsd: assetUsdEquivalentAmount
        });
      }

      setAssetsData(accountBalances);
    }
  }, [isLoadingAccountBalances, accountBalances]);

  const [hideLowBalances, setHideLowBalances] = useState<boolean>(false);
  const changeHideLowBalances = (value: boolean) => {
    setHideLowBalances(value);
    const assets = accountBalances.filter(
      (asset: any) =>
        !value || (value && (!asset['pctChange24hInUsd'] || (asset['pctChange24hInUsd'] && !asset['pctChange24hInUsd'].startsWith('-'))))
    );
    setAssetsData(assets);
  };

  const [lastUpdated, setLastUpdated] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  const [dateRangeProps, setDateRangeProps] = useState<{ from: Moment | null; to: Moment | null }>({
    from: null,
    to: null
  });
  const handleChangeDateRange = (value: { from: Moment | null; to: Moment | null }) => {
    setDateRangeProps(value);
  };
  const [searchTransaction, setSearchTransaction] = useState<string | undefined>(undefined);
  const {
    transactions,
    isLoadingAccountTransactions,
    nextToken,
    preToken,
    next,
    prev,
    reloadAccountTransactions,
    refreshAccountTransactions,
    clearCurrentPageToken
  } = useAccountTransaction({
    accountId: account,
    dateRangeFrom: dateRangeProps?.from ? moment(dateRangeProps?.from)?.toISOString() : undefined,
    dateRangeTo: dateRangeProps?.to ? moment(dateRangeProps?.to)?.toISOString() : undefined,
    search: searchTransaction
  });

  const { cardAccounts, isLoadingCardAccounts } = record?.cardAccountId
    ? useCard({ cardAccountId: record?.cardAccountId })
    : { cardAccounts: [], isLoadingCardAccounts: true };
  useEffect(() => {
    if (!isLoadingCardAccounts) {
      let balanceHkdEquivalentAmount = 0;
      let balanceUsdEquivalentAmount = 0;
      if (cardAccounts && cardAccounts.length) {
        cardAccounts.forEach((cardAccount) => {
          balanceHkdEquivalentAmount += cardAccount?.balanceHkdEquivalent || 0;
          balanceUsdEquivalentAmount += cardAccount?.balanceUsdEquivalent || 0;
        });
        setAccountsOverview({
          ...accountsOverview,
          creditBalanceInHkd: balanceHkdEquivalentAmount,
          creditBalanceInUsd: balanceUsdEquivalentAmount
        });
      } else {
        setAccountsOverview({
          ...accountsOverview,
          creditBalanceInHkd: balanceHkdEquivalentAmount,
          creditBalanceInUsd: balanceUsdEquivalentAmount
        });
      }
    }
  }, [isLoadingCardAccounts, cardAccounts]);

  const [netAssetValues, setNetAssetValues] = useState<{ navInHkd: number; navInUsd: number }>({
    navInHkd: 0,
    navInUsd: 0
  });
  useEffect(() => {
    setNetAssetValues({
      navInHkd: accountsOverview.assetBalanceInHkd - accountsOverview.creditBalanceInHkd,
      navInUsd: accountsOverview.assetBalanceInUsd - accountsOverview.creditBalanceInUsd
    });
  }, [
    accountsOverview.assetBalanceInHkd,
    accountsOverview.assetBalanceInUsd,
    accountsOverview.creditBalanceInHkd,
    accountsOverview.creditBalanceInUsd
  ]);

  const funcAccountsTransfers = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    accountsTransfers(data, onSuccess, onError);
  };

  return (
    <View sx={{ ...styles.contentContainer, ...(layout.screenSize === ScreenSize.Tablet ? { flexDirection: 'column' } : {}) }}>
      <View sx={{ gap: '$5', ...(layout.screenSize === ScreenSize.Tablet ? {} : { width: '66%' }) }}>
        <View sx={styles.overviewCard}>
          <PageItem sx={styles.card}>
            <View sx={styles.overviewHeader}>
              <View sx={styles.overviewHeaderLeft}>
                <H5 bold>{t('card-account-management::account-page.title--overview')}</H5>
                <CurrencySelector options={CurrenciesValues} onValueChange={(value) => setCurrency(value)} />
                {/* <UniSlideRadioButton data={CurrenciesRadios} buttonWidth={52} onValueChange={(value) => setCurrency(value)} /> */}
              </View>
              <View sx={styles.overviewOperations}>
                <UtxHasFeature feature={'ACCESS_TRANSFER'} variation={'enabled'}>
                  <View sx={styles.accountActionBtn}>
                    <TextBody
                      sx={styles.accountButton}
                      onPress={() => {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.AccountsTransfers,
                          params: {
                            account: account,
                            tab: tab,
                            record: record,
                            func: funcAccountsTransfers,
                            refresh: () => {
                              reloadAccountBalances();
                              refreshAccountTransactions();
                            }
                          }
                        });
                      }}>
                      <Icon name="transferUTGLWeb" />
                    </TextBody>
                    <TextBody semiBold>{t('account-transfer::glossary.button-transfer')}</TextBody>
                  </View>
                </UtxHasFeature>
                <UtxHasFeature feature={'ACCESS_DIGITAL_DEPOSIT'} variation={'enabled'} sx={{ flex: 'none' }}>
                  <View sx={styles.accountActionBtn}>
                    <TextBody
                      sx={styles.accountButton}
                      onPress={() => {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.DigitalDeposit,
                          params: {
                            account: account,
                            record: record,
                            refresh: () => {}
                          }
                        });
                      }}>
                      <Icon name="injectUTGLWeb" />
                    </TextBody>
                    <TextBody semiBold sx={{ textAlign: 'center' }}>
                      {t('digital-injection::glossary.digital-injection')}
                    </TextBody>
                  </View>
                </UtxHasFeature>
                {/* <View sx={styles.accountActionBtn}>
                  <TextBody sx={styles.accountButton} onPress={() => console.log('Inject')}>
                    <Icon name="injectUTGLWeb" />
                  </TextBody>
                  <TextBody semiBold>{t('card-account-management::account-page.button--inject')}</TextBody>
                </View>
                <View sx={styles.accountActionBtn}>
                  <TextBody sx={styles.accountButton} onPress={() => console.log('Withdraw')}>
                    <Icon name="withdrawUTGLWeb" />
                  </TextBody>
                  <TextBody semiBold>{t('card-account-management::account-page.button--withdraw')}</TextBody>
                </View>
                <View sx={styles.accountActionBtn}>
                  <TextBody sx={styles.accountButton} onPress={() => console.log('Transfer')}>
                    <Icon name="transferUTGLWeb" />
                  </TextBody>
                  <TextBody semiBold>{t('card-account-management::account-page.button--transfer')}</TextBody>
                </View>
                <View sx={styles.accountActionBtn}>
                  <TextBody sx={styles.accountButton} onPress={() => console.log('Exchange')}>
                    <Icon name="exchangeUTGLWeb" />
                  </TextBody>
                  <TextBody semiBold>{t('card-account-management::account-page.button--exchange')}</TextBody>
                </View> */}
                <UtxHasFeature feature={'UNIONPAY_REMITTANCE'} variation={'beta'}>
                  <View sx={styles.accountActionBtn}>
                    <TextBody
                      sx={styles.accountButton}
                      onPress={() => {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.UnionPay,
                          params: {
                            unionType: UnionType.ACCOUNT,
                            refresh: () => {
                              refreshAccountTransactions();
                            }
                          }
                        });
                      }}>
                      <Icon name="unionPayBP" width={30} height={30} />
                    </TextBody>
                    <TextBody semiBold>{t('unionPay::payment-instruction.glossary.unionpay')}</TextBody>
                  </View>
                </UtxHasFeature>
              </View>
            </View>

            <View sx={styles.overviewContent}>
              <Row sx={{ alignItems: 'center', gap: '$1' }}>
                <TextBody semiBold secondary>
                  {t('card-account-management::account-page.text--net-asset-value', { currency })}
                </TextBody>
                <UniIconButton icon={show ? 'hide' : 'show'} onPress={(e) => showChange(!show)} />
              </Row>
              <Row sx={{ mt: '$1' }}>
                <H3 bold sx={{ color: theme?.colors?.$secondary as string }}>
                  {hideShowBalance(
                    formatAmountByDecimal({
                      value: currency == 'HKD' ? netAssetValues.navInHkd : netAssetValues.navInUsd,
                      suffix: currency,
                      decimals: 2
                    })
                  )}
                </H3>
              </Row>
              <Row sx={{ marginTop: '$7' }}>
                <Row sx={{ alignItems: 'center', gap: '$4', width: '33%' }}>
                  <Icon name="accountAsset" width={30} height={30} />
                  <View>
                    <TextBody semiBold secondary sx={{ mb: '$1' }}>
                      {t('card-account-management::account-page.text--asset')}
                    </TextBody>
                    <H6 bold>
                      {hideShowBalance(
                        formatAmountByDecimal({
                          value: currency == 'HKD' ? accountsOverview.assetBalanceInHkd : accountsOverview.assetBalanceInUsd,
                          suffix: currency,
                          decimals: 2
                        })
                      )}
                    </H6>
                  </View>
                </Row>
                <Row sx={{ alignItems: 'center', gap: '$4', width: '33%' }}>
                  <Icon name="accountBalance" width={30} height={30} />
                  <View>
                    <TextBody semiBold secondary sx={{ mb: '$1' }}>
                      {t('card-account-management::account-page.text--card-account-balance')}
                    </TextBody>
                    <H6 bold>
                      {hideShowBalance(
                        formatAmountByDecimal({
                          value:
                            currency == 'HKD'
                              ? accountsOverview.creditBalanceInHkd
                                  .toString()
                                  .substring(isNegative(accountsOverview.creditBalanceInHkd.toString()) ? 1 : 0)
                              : accountsOverview.creditBalanceInUsd
                                  .toString()
                                  .substring(isNegative(accountsOverview.creditBalanceInUsd.toString()) ? 1 : 0),
                          suffix: `${currency} ${isNegative(accountsOverview.creditBalanceInHkd.toString()) ? '[CR]' : ''}`,
                          decimals: 2
                        })
                      )}
                    </H6>
                  </View>
                </Row>
                <Row sx={{ alignItems: 'center', gap: '$4', width: '33%' }}></Row>
              </Row>
            </View>
          </PageItem>
        </View>
        <View sx={styles.transactionCard}>
          <PageItem sx={styles.card}>
            <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <H5 bold>{t('card-account-management::account-page.title--transaction-history')}</H5>
              <UniButton
                wrapperSx={{ width: 240 }}
                labelSx={{ color: '$black' }}
                label={t('card-account-management::account-page.button--download-transaction-report')}
                onPress={() =>
                  navigationAny.navigate(RootStackRoutes.AccountManagement, {
                    screen: AccountManagementStackRoutes.DownloadTransactionReport,
                    params: {
                      screen: DownloadTransactionReportStackRoutes.DownloadAccountTransactionReport,
                      params: { walletId: account }
                    }
                  })
                }
              />
            </View>
            <View sx={styles.transactionHeader}>
              <Row sx={{ flexDirection: 'row', gap: '$3', height: 36 }}>
                <UniIconButton
                  sx={{
                    borderWidth: 1,
                    borderColor: theme?.colors?.$borderButton as string,
                    borderRadius: 5,
                    width: 36,
                    justifyContent: 'center'
                  }}
                  icon="refresh"
                  onPress={() => {
                    handleChangeDateRange({ from: null, to: null });
                    setSearchTransaction(undefined);
                    refreshAccountTransactions();
                  }}
                />
                <DateRangePicker
                  dateRangeProps={dateRangeProps}
                  onValueChange={(value) => {
                    clearCurrentPageToken();
                    handleChangeDateRange({
                      ...dateRangeProps,
                      from: value.from ? moment(value.from) : null,
                      to: value.to ? moment(value.to) : null
                    });
                  }}
                />
              </Row>
              <UniInput
                value={searchTransaction}
                wrapperSx={{ flexDirection: 'row', justifyContent: 'flex-end' }}
                sx={{ width: 300 }}
                size="default"
                prefixIcon="search"
                placeholder={t('card-account-management::card-details.text--search-by-desc-ref-id')}
                onValueChange={(value) => {
                  clearCurrentPageToken();
                  setSearchTransaction(value);
                }}
              />
            </View>
            <View sx={styles.transactionContent}>
              <TableView<any>
                data={transactions}
                rowVerticalAlign="flex-start"
                loading={isLoadingAccountTransactions}
                columns={[
                  {
                    title: t('card-account-management::account-page.text--date-ref-id'),
                    index: 'createdAt',
                    minWidth: 200,
                    render: (item) => (
                      <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center', pl: '$2' }}>
                        <Icon name={item.asset.toLowerCase()} width={28} height={28} />
                        <View sx={{ gap: '$1' }}>
                          <TextBody sx={{ color: '$textPrimary' }}>{moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}</TextBody>
                          <TextCaption sx={{ color: '$textPlaceholder' }}>{item.intentRefId}</TextCaption>
                        </View>
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::account-page.text--description'),
                    index: 'description',
                    render: (item) => (
                      <View sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <TextBody>{item.description || '-'}</TextBody>
                        <View sx={{ width: '$10', height: '$10', justifyContent: 'center', zIndex: 1 }}>
                          {!!item.xmetadata && (
                            <Tooltip
                              content={
                                <TextBody sx={styles.tooltipText}>
                                  {t('card-account-management::card-details.text--view-xmetadata')}
                                </TextBody>
                              }
                              position="middle"
                              contentTextMinWidth={119}
                              showUnderline={false}>
                              <UniIconButton
                                icon="info"
                                onPress={(e) => {
                                  navigation.navigate(RootStackRoutes.AccountManagement, {
                                    screen: AccountManagementStackRoutes.Xmetadata,
                                    params: { xmetadata: item.xmetadata }
                                  });
                                }}
                              />
                            </Tooltip>
                          )}
                        </View>
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::account-page.text--debit-credit'),
                    index: 'amount',
                    align: 'right',
                    render: (item) => (
                      <TextBody semiBold success={!isNegative(item.amount) && true} danger={isNegative(item.amount) && true}>
                        {hideShowBalance(
                          formatAmountByDecimal({
                            value: Math.abs(item?.amount),
                            suffix: item.asset,
                            prefix: !isNegative(item.amount) ? '+' : '-',
                            decimals: 2
                          })
                        )}
                      </TextBody>
                    )
                  },
                  {
                    title: t('card-account-management::account-page.text--balance'),
                    index: 'balance',
                    align: 'right',
                    render: (item) => (
                      <TextBody semiBold sx={{ color: theme?.colors?.$secondary as string }}>
                        {hideShowBalance(formatAmountByDecimal({ value: item.balance || 0, suffix: item.asset, decimals: 2 }))}
                      </TextBody>
                    )
                  }
                ]}
                emptyComponent={<NoData />}
              />
            </View>
            <View sx={styles.transactionFooter}>
              <Row sx={{ flexDirection: 'row', gap: '$1', alignItems: 'center' }}>
                {/* <Icon name="clock1" width={16} height={16} />
                <TextCaption sx={{ color: '$textPlaceholder' }}>{lastUpdated}</TextCaption> */}
                <LastUpdated dateTime={new Date().toISOString()} />
              </Row>
              <TokenPagination prevPageToken={preToken} nextPageToken={nextToken} onNextPage={next} onPrevPage={prev} />
            </View>
          </PageItem>
        </View>
      </View>
      <View sx={{ ...styles.assetCard, ...(layout.screenSize === ScreenSize.Tablet ? {} : { width: '33%' }) }}>
        <PageItem sx={styles.card}>
          <View sx={styles.assetHeader}>
            <H5 bold>{t('card-account-management::account-page.text--assets')}</H5>
            <UniInput
              wrapperSx={{ flexDirection: 'row', justifyContent: 'flex-end' }}
              sx={{ width: 220, flexDirection: 'column', justifyContent: 'center' }}
              size="default"
              prefixIcon="search"
              placeholder={t('card-account-management::account-page.text--search-by-asset')}
              onValueChange={(value) => setSearchAsset(value)}
            />
          </View>
          <View sx={styles.assetOperation}>
            <UniCheckbox
              label={t('card-account-management::account-page.text--hide-low-balances')}
              value={hideLowBalances}
              verticalAlignCenter
              onValueChange={(value) => changeHideLowBalances(value)}
            />
            <Divider />
          </View>
          <View sx={styles.assetContent}>
            {assetsData && assetsData.length ? (
              assetsData.map((item, i) => (
                <Row key={i} sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', pt: '$2', pb: '$2' }}>
                  <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center' }}>
                    {item?.iconUrl ? (
                      <Image sx={{ width: 28, height: 28 }} source={{ uri: item?.iconUrl }} />
                    ) : (
                      <Icon name={item.asset?.toLowerCase()} width={28} height={28} />
                    )}
                    <View>
                      <TextTitle semiBold>{item.asset}</TextTitle>
                      <Row sx={{ alignItems: 'center', gap: '$2' }}>
                        <Tooltip
                          content={
                            <TextBody sx={styles.tooltipText}>
                              {formatAmountByDecimals({
                                value: Number(item.usdRate).toString(),
                                prefix: '1 USD ≈',
                                suffix: item.asset
                              })}
                            </TextBody>
                          }
                          position="middle"
                          contentTextMinWidth={120}>
                          <TextCaption secondary>
                            $
                            {formatAmountByDecimals({
                              value: Number(item.usdRate).toString()
                            })}
                          </TextCaption>
                        </Tooltip>
                        <Row sx={{ alignItems: 'center' }}>
                          <TextCaption
                            success={!isNegative(item.pctChange24hInUsd || '')}
                            danger={isNegative(item.pctChange24hInUsd || '')}>
                            (
                          </TextCaption>
                          <Icon
                            name={isNegative(item.pctChange24hInUsd || '') ? 'arrowDownPoint' : 'arrowUpPoint'}
                            color={
                              isNegative(item.pctChange24hInUsd || '')
                                ? (theme?.colors?.$danger as string)
                                : (theme?.colors?.$success as string)
                            }
                          />
                          <TextCaption
                            success={!isNegative(item.pctChange24hInUsd || '')}
                            danger={isNegative(item.pctChange24hInUsd || '')}>
                            {formatAmountByDecimal({ value: item.pctChange24hInUsd || 0, decimals: 2 })}
                          </TextCaption>
                          <TextCaption
                            success={!isNegative(item.pctChange24hInUsd || '')}
                            danger={isNegative(item.pctChange24hInUsd || '')}>
                            %)
                          </TextCaption>
                        </Row>
                      </Row>
                    </View>
                  </View>
                  <View sx={{ alignItems: 'flex-end' }}>
                    <TextBody semiBold sx={{ color: theme?.colors?.$secondary as string, mb: '$1' }}>
                      {formatAmountByDecimals({ value: item.balance || 0, suffix: item.asset })}
                    </TextBody>
                    <TextBody secondary>
                      ≈ {formatAmountByDecimal({ value: item.balanceUsdEquivalent || 0, suffix: 'USD', decimals: 2 })}
                    </TextBody>
                  </View>
                </Row>
              ))
            ) : (
              <View></View>
            )}
          </View>
        </PageItem>
      </View>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  overviewCard = 'overviewCard',
  assetCard = 'assetCard',
  transactionCard = 'transactionCard',
  overviewHeader = 'overviewHeader',
  overviewContent = 'overviewContent',
  overviewOperations = 'overviewOperations',
  assetHeader = 'assetHeader',
  assetOperation = 'assetOperation',
  assetContent = 'assetContent',
  transactionHeader = 'transactionHeader',
  transactionContent = 'transactionContent',
  transactionFooter = 'transactionFooter',
  accountActionBtn = 'accountActionBtn',
  accountButton = 'accountButton',
  overviewHeaderLeft = 'overviewHeaderLeft',
  tooltipText = 'tooltipText',
  noData = 'noData'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '$5'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '$7',
    borderRadius: 15
  },
  overviewCard: {},
  overviewHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  overviewContent: { mt: '$6', flexDirection: 'column' },
  overviewOperations: { flexDirection: 'row', gap: '$3', height: '$15' },
  assetCard: {},
  assetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  assetOperation: { mt: '$6', flexDirection: 'column', gap: '$3' },
  assetContent: { mt: '$2', flexDirection: 'column', gap: '$3' },
  transactionCard: {},
  transactionHeader: { mt: '$7', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 },
  transactionContent: { mt: '$5' },
  transactionFooter: { mt: '$5', flexDirection: 'row', justifyContent: 'space-between' },
  accountActionBtn: {
    // width: 100,
    alignItems: 'center',
    gap: '$1'
  },
  accountButton: {
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 100,
    backgroundColor: '$filterSelected'
  },
  overviewHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$5'
  },
  tooltipText: {
    color: '$textTooltip',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  noData: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '$3',
    minHeight: 250
  }
};
